import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home as HomeIcon } from 'lucide-react';
import api from '@/lib/api';

// Static label map for route segments.
const SEGMENT_LABELS = {
  projects: 'Projetos',
  clients: 'Clientes',
  financial: 'Financeiro',
  budgets: 'Orçamentos',
  reports: 'Relatórios',
  analytics: 'Analytics',
  users: 'Usuários',
  'task-calendar': 'Atividades',
  'project-templates': 'Templates',
  'meus-projetos': 'Meus Projetos',
  'meu-cadastro': 'Meu Cadastro',
  'deadline-control': 'Controle de Prazos',
  prioridades: 'Prioridades',
  health: 'Diagnóstico',
  mcp: 'Conector Claude',
  evolution: 'Evolução',
  calendar: 'Calendário',
};

// Routes where we hide the breadcrumb (root/landing pages — already at top level)
const HIDDEN_PATHS = new Set(['/home', '/', '/meus-projetos', '/login']);

const isProjectId = (s) => /^proj_[a-f0-9]+$/i.test(s);
const isPhaseId = (s) => /^phase_[a-f0-9]+$/i.test(s);
const isTaskId = (s) => /^task_[a-f0-9]+$/i.test(s);

/**
 * Auto-generated breadcrumb based on the URL path.
 *
 * Examples:
 *   /projects                  → Início > Projetos
 *   /projects/proj_abc123      → Início > Projetos > {Nome do Projeto}
 *   /clients                   → Início > Clientes
 *   /home                      → (hidden)
 */
export default function Breadcrumb({ user }) {
  const location = useLocation();
  const [projectName, setProjectName] = useState(null);

  const isClient = user?.role?.toUpperCase() === 'CLIENT';
  const homePath = isClient ? '/meus-projetos' : '/home';

  const segments = location.pathname.split('/').filter(Boolean);

  // Fetch project name when URL contains a project_id segment
  useEffect(() => {
    const projSeg = segments.find(isProjectId);
    if (!projSeg) {
      setProjectName(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projSeg}`);
        if (!cancelled) setProjectName(data?.name || projSeg);
      } catch {
        if (!cancelled) setProjectName(projSeg);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (HIDDEN_PATHS.has(location.pathname)) return null;
  if (segments.length === 0) return null;

  // Build crumbs left-to-right, accumulating href
  const crumbs = [];
  let href = '';
  segments.forEach((seg, idx) => {
    href += `/${seg}`;
    let label = SEGMENT_LABELS[seg];
    if (!label) {
      if (isProjectId(seg)) label = projectName || 'Projeto';
      else if (isPhaseId(seg)) label = 'Fase';
      else if (isTaskId(seg)) label = 'Tarefa';
      else label = seg.charAt(0).toUpperCase() + seg.slice(1);
    }
    crumbs.push({ href, label, isLast: idx === segments.length - 1 });
  });

  return (
    <nav aria-label="Breadcrumb" data-testid="breadcrumb" className="text-sm text-gray-500 mb-4">
      <ol className="flex items-center flex-wrap gap-1">
        <li>
          <Link
            to={homePath}
            className="flex items-center gap-1 hover:text-emerald-700 transition-colors"
            data-testid="breadcrumb-home"
          >
            <HomeIcon className="w-3.5 h-3.5" />
            <span>Início</span>
          </Link>
        </li>
        {crumbs.map((c) => (
          <li key={c.href} className="flex items-center gap-1">
            <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
            {c.isLast ? (
              <span className="text-gray-900 font-medium truncate max-w-xs" data-testid="breadcrumb-current">{c.label}</span>
            ) : (
              <Link to={c.href} className="hover:text-emerald-700 transition-colors truncate max-w-xs">
                {c.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
